import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from "@angular/core";
import { Router, RouterLink, RouterOutlet } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import {
  KsgAppLayout,
  KsgTopbar,
  KsgThemeToggle,
  KsgSidebarFooterDef,
  KsgSessionTimer,
  KsgLanguageSelector,
  KsgUserPanel,
  KsgButton,
  KsgToast,
  ksgRouterActiveUrl,
  type KsgSidebarSection,
} from "@ksg/ui";
import { SessionService } from "../core/session/session.service";
import { AuthService } from "../core/auth/auth.service";
import { BrandLogo } from "./brand-logo";
import { CompanySwitcher } from "./component/company-switcher/company-switcher";
import { TourButton } from "../shared/tour/tour-button";
import { TourOverlay } from "../shared/tour/tour-overlay";
import { DemoNotice } from "../shared/demo/demo-notice";

interface NavEntry {
  key: string;
  icon: string;
  url: string;
}

const NAV: readonly NavEntry[] = [
  { key: "dashboard", icon: "speed", url: "/" },
  { key: "employees", icon: "pi-users", url: "/hr" },
  { key: "payroll", icon: "pi-wallet", url: "/hr/payroll" },
  { key: "approvals", icon: "pi-check-square", url: "/hr/approvals" },
  { key: "tutorial", icon: "pi-compass", url: "/samouczek" },
  { key: "support", icon: "pi-flag", url: "/zgloszenia" },
] as const;

@Component({
  selector: "app-shell",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    TranslocoPipe,
    KsgAppLayout,
    KsgTopbar,
    KsgThemeToggle,
    KsgSidebarFooterDef,
    KsgSessionTimer,
    KsgLanguageSelector,
    KsgUserPanel,
    KsgButton,
    KsgToast,
    BrandLogo,
    CompanySwitcher,
    TourButton,
    TourOverlay,
    DemoNotice,
  ],
  templateUrl: "./app-shell.html",
  styleUrl: "./app-shell.scss",
})
export class AppShell {
  protected readonly session = inject(SessionService);
  protected readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  private readonly rawUrl = ksgRouterActiveUrl();

  private readonly i18nReady = toSignal(
    this.transloco.selectTranslate("menu.dashboard"),
  );

  private static readonly ROOTS = NAV.map((n) => n.url).filter(
    (u) => u !== "/",
  );

  protected readonly activeUrl = computed(() => {
    const u = this.rawUrl().split("?")[0].split("#")[0];
    if (u === "/" || u === "") return "/";
    return AppShell.ROOTS.find((r) => u === r || u.startsWith(r + "/")) ?? u;
  });

  protected readonly sections = computed<KsgSidebarSection[]>(() => {
    this.i18nReady();
    const t = (k: string) => this.transloco.translate(k);
    return [
      {
        items: NAV.map((n) => ({
          label: t("menu." + n.key),
          icon: n.icon,
          url: n.url,
          command: () => void this.router.navigate([n.url]),
        })),
      },
    ];
  });
}
